"use client"

import { useState, useEffect } from "react"
import { useGoogleReCaptcha } from "@/lib/recaptcha-client"
import { usePathname } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Mail, Loader2, Check } from "lucide-react"
import { isValidLocale, type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"

export function NewsletterForm() {
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState("")
  const pathname = usePathname()

  // Get current locale from pathname
  const getCurrentLocale = (): Locale => {
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    return isValidLocale(firstSegment) ? firstSegment : 'tr'
  }

  const currentLocale = getCurrentLocale()
  const t = getTranslations(currentLocale)
  const { executeRecaptcha } = useGoogleReCaptcha()

  // Clear error when user starts typing
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (error) {
      setError("")
    }
  }

  // Clear state when route changes (sayfa değişince hata/success temizlensin)
  useEffect(() => {
    setEmail("")
    setError("")
    setSuccess(false)
    setLoading(false)
  }, [pathname])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        setError(t.newsletter.invalidEmail)
        setLoading(false)
        return
      }

      // Get reCAPTCHA token
      let recaptchaToken = ""
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("newsletter_form")
      }

      // API endpoint'e istek gönder
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          recaptchaToken,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || t.newsletter.error)
        setEmail("") // Clear email field on error

        // Hata mesajını birkaç saniye sonra otomatik kaldır
        setTimeout(() => {
          setError("")
        }, 4000)

        setLoading(false)
        return
      }

      setSuccess(true)
      setEmail("")
      
      // 3 saniye sonra success mesajını kaldır
      setTimeout(() => {
        setSuccess(false)
      }, 3000)
    } catch (err) {
      setError(t.newsletter.error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" suppressHydrationWarning>
      <div className="flex flex-col sm:flex-row gap-2" suppressHydrationWarning>
        <div className="relative flex-1" suppressHydrationWarning>
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder={t.newsletter.placeholder}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[rgb(3,74,166)] focus:border-transparent text-sm"
            required
            disabled={loading || success}
          />
        </div>
        <Button
          type="submit"
          disabled={loading || success}
          className="bg-[rgb(3,74,166)] hover:bg-[rgb(2,40,89)] text-white px-6 py-2.5 h-auto whitespace-nowrap"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t.newsletter.sending}
            </>
          ) : success ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              {t.newsletter.sent}
            </>
          ) : (
            t.newsletter.button
          )}
        </Button>
      </div>
      {error && (
        <p className="text-xs text-red-400" suppressHydrationWarning>{error}</p>
      )}
      {success && (
        <p className="text-xs text-green-400" suppressHydrationWarning>
          {t.newsletter.success}
        </p>
      )}
    </form>
  )
}

